import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthorization } from '../contexts/AuthorizationContext';
import LoadingSpinner from './LoadingSpinner';

interface PrivilegeGateProps {
  privilege: string;
  children: React.ReactNode;
}

const PrivilegeGate: React.FC<PrivilegeGateProps> = ({ privilege, children }) => {
  const location = useLocation();
  const { hasPrivilege, loading, lastUpdated, error } = useAuthorization();
  const [graceElapsed, setGraceElapsed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Give the authorization context a grace period to fetch privileges
  // before deciding the user truly lacks access. This prevents false
  // redirects to /unauthorized on page reloads when the org context
  // hasn't finished restoring yet.
  useEffect(() => {
    if (!lastUpdated && !error) {
      timerRef.current = setTimeout(() => setGraceElapsed(true), 3000);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
    setGraceElapsed(false);
  }, [lastUpdated, error]);

  if (loading || (!lastUpdated && !error && !graceElapsed)) {
    return <LoadingSpinner />;
  }

  if (!hasPrivilege(privilege)) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default PrivilegeGate;
